import { PrimaryControlsStripLeading } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import {
  ToolbarIconButton,
  ToolbarMoreActions,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
  ToolbarPopoverSwitchRow,
  type ToolbarPopoverChoiceOption,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { LevelsFilterMenu } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.levels-filter-menu";
import { type LevelsTopbarViewMode } from "#view-button";
import { BrowseLevelsLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useScopedPathname } from "@-/com.npmjs.expo-router";
import { useBrowseLevelsStudyGraphemesQuery } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { LevelsTopbarCompletionFilter, completionFilterFromPathname } from "#completion-filter";

const VIEW_MODE_OPTIONS: readonly ToolbarPopoverChoiceOption<LevelsTopbarViewMode>[] = [
  { label: "Glyphs", testID: "levels-display-glyph-tiles", value: "glyph-tiles" },
  { label: "Previews", testID: "levels-display-detail-tiles", value: "detail-tiles" },
];

/**
 * The Levels toolbar controls, shared by every levels sub-route, float over the list.
 */
export function BrowseLevelsToolbarActions() {
  const [showSlideLessons, setShowSlideLessons] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.showSlideLessons,
    (c) => c.setShowSlideLessons,
  );
  const completionFilter = completionFilterFromPathname(useScopedPathname("/browse"));
  const [viewMode, setViewMode] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.viewMode,
    (c) => c.setViewMode,
  );
  // Completion-dependent controls (the completion filter) wait for the
  // study data; the level catalog behind them renders progressively in the meantime.
  const { isPending: isStudyDataPending } = useBrowseLevelsStudyGraphemesQuery();

  if (isStudyDataPending) {
    return (
      <PrimaryControlsStripLeading>
        <ToolbarIconButton
          accessibilityLabel="Loading levels"
          disabled
          isLoading
          name="MdRefresh"
          size="menu"
        />
      </PrimaryControlsStripLeading>
    );
  }

  return (
    <PrimaryControlsStripLeading>
      <ToolbarMoreActions className="w-full">
        <ToolbarPopoverButton
          accessibilityLabel="Level display options"
          label="View"
          // The glyph mirrors the CURRENT tile style; the popover is where the other one is chosen.
          name={viewMode === "glyph-tiles" ? "BsGrid3X3Gap" : "BsGrid"}
        >
          <ToolbarPopoverChoiceRow
            label="Tiles"
            onValueChange={setViewMode}
            options={VIEW_MODE_OPTIONS}
            value={viewMode}
          />
          <ToolbarPopoverSwitchRow
            checked={showSlideLessons}
            label="Show slides"
            onCheckedChange={setShowSlideLessons}
            testID="levels-show-slide-lessons"
          />
        </ToolbarPopoverButton>
        <LevelsFilterMenu selected={completionFilter === "incomplete"}>
          <LevelsTopbarCompletionFilter />
        </LevelsFilterMenu>
      </ToolbarMoreActions>
    </PrimaryControlsStripLeading>
  );
}
